import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Grid,
  Header,
  List,
  Loader,
  Message,
  Segment,
} from "semantic-ui-react";

import Notes from "../note/notes";
import PayAction from "./pay";
import PayOutAction from "./payout";
import ChargeList from "../billing/charge-list";
import AttributeList from "../billing/attribute/list";

import { ErrorWrap } from "../../helpers/error";
import { BackButton } from "../common/back-button";
import { ProfileCard } from "../../components/estate/profile-card";
import { RoleRequired } from "../auth/role-required";
import { DetailsHeader } from "./header";
import { AddCustomerModal } from "./add-customer-modal.js";
import { EstateEditModal } from "./estate-edit-modal.js";
import { SettlementListEstate } from "../billing/settlement/list-estate";
import { ChargeEdit } from "../../components/billing/charge-edit";
import { DisplayCreatedSettlement } from "./settlement-created";
import { SendRemindersModal } from "./send-reminders-modal";

import { getEstate } from "../../redux/modules/estates";
import { filterByProp, getById } from "../../redux/restdux/utils";
import { deleteEstateCustomer } from "../../redux/modules/estate-customers";
import { t } from "../../constant/translations";

import { Switch } from "react-router";
import { reverse, routes } from "../../routes";
import { Route } from "react-router-dom";
import { SettlementModal } from "./settlement-modal";
import styled from "styled-components";
import { permissions } from "../../constant/permissions";
import { getActionType } from "../../redux/restdux/action-types";

const MultiButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: space-evenly;
  padding: 0.5rem;
  //text-wrap: avoid;
`;

const AddProfileButtonWrapper = styled.div`
  overflow: hidden;
`;

const ButtonWrapper = styled.div`
  margin: 5px;
`;

const InfoWrapper = styled.div`
  margin: 10px 0 0 10px !important;
`;

const StyledHeader = styled(Header)({
  margin: "5px 10px!important",
});

const StyledHeaderAttr = styled(StyledHeader)`
  display: inline;
`;

const StyledNotes = styled(Notes)({
  margin: "0 0 10px 0!important",
});

const StyledColumn = styled(Grid.Column)`
  margin-bottom: 1rem !important;
`;

const FlexRowSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;

  > button {
    margin: 0 0 !important;
  }
`;

const EstateDetails = () => {
  const params = useParams();
  const estateId = parseInt(params.estateId);

  const block = useSelector((state) => state.blocks.selected);
  const estate = useSelector((state) =>
    getById(estateId, state.estates.entities)
  );
  const profiles = useSelector((state) => state.profile.entities);
  const error = useSelector((state) => new ErrorWrap(state.estates.error));
  const estateCustomers = useSelector(
    (state) => state.estatecustomers.entities
  );
  const estateCustomersError = useSelector((state) =>
    ErrorWrap.fromError(state.estatecustomers.error)
  );
  const appSettings = useSelector((state) => state.appSettings.data);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!estate) {
      dispatch(getEstate(estateId));
    }
  }, [estateId]);

  const handleDeleteEstateCustomer = (data) => {
    dispatch(deleteEstateCustomer(data));
  };

  if (error.exists() && error.hasType(getActionType("estate", "FETCH_ERROR"))) {
    return <div>{error.getGlobal()}</div>;
  }

  if (!estate) {
    return <Loader active={true} />;
  }

  const customers = filterByProp(estateCustomers, { estate: estate.id });
  const profileCards = [];
  customers.forEach((customer) => {
    const profile = getById(customer.customer, profiles);
    if (profile) {
      profileCards.push(
        <ProfileCard
          key={profile.id}
          type={customer.type}
          profile={profile}
          showDelete={true}
          deleteError={estateCustomersError}
          deleteAction={() => handleDeleteEstateCustomer({ id: customer.id })}
          estate={estate}
        />
      );
    }
  });

  return (
    <React.Fragment>
      <Switch>
        <Route
          path={routes.estateSettlementDetails}
          render={(props) => {
            const returnURL = reverse(routes.estateDetails, {
              blockId: block.id,
              estateId: estate.id,
            });
            return <SettlementModal returnURL={returnURL} {...props} />;
          }}
        />
      </Switch>
      <Grid padded>
        <Grid.Row>
          <StyledColumn largeScreen={4} computer={6} tablet={16}>
            <FlexRowSpaceBetween>
              <BackButton />
              <RoleRequired staff={true} permission={permissions.estateEdit}>
                <EstateEditModal entity={estate} />
              </RoleRequired>
            </FlexRowSpaceBetween>
            <Segment.Group>
              <Segment>
                <StyledHeader
                  forwardedAs="h4"
                  content={`${estate.name}`}
                  subheader={`${t("estate.client_number")} ${estate.vendor_id}`}
                />
                <InfoWrapper>
                  <List bulleted>
                    <List.Item>{t(`estateTypes.${[estate.etype]}`)}</List.Item>
                    {estate.floor && (
                      <List.Item>
                        {t("estate.floor")} {estate.floor}
                      </List.Item>
                    )}
                    {estate.preferred_payment_method && (
                      <List.Item>
                        {t("settlement.payment_method")}:{" "}
                        {t(
                          `paymentMethods.${[estate.preferred_payment_method]}`
                        ) || estate.preferred_payment_method}
                      </List.Item>
                    )}
                  </List>
                </InfoWrapper>
              </Segment>
              <MultiButtonsWrapper>
                <RoleRequired
                  staff={true}
                  permission={permissions.estateSettleAmount}
                >
                  <ButtonWrapper>
                    <DisplayCreatedSettlement />
                    <PayAction estate={estate} triggerText={t("estate.pay")} />
                  </ButtonWrapper>
                  <ButtonWrapper>
                    <PayOutAction
                      estate={estate}
                      triggerText={t("estate.return_deposit")}
                    />
                  </ButtonWrapper>
                </RoleRequired>
                <RoleRequired
                  staff={true}
                  permission={permissions.customerEdit}
                >
                  <ButtonWrapper>
                    <ChargeEdit
                      estate={estate}
                      trigger_props={{
                        color: "blue",
                        icon: "plus",
                        content: t("estate.add_obligation"),
                        basic: true,
                      }}
                    />
                  </ButtonWrapper>
                </RoleRequired>
                {appSettings?.features?.send_notifications && (
                  <RoleRequired
                    staff={true}
                    permission={permissions.estateEdit}
                  >
                    <ButtonWrapper>
                      <SendRemindersModal estate={estate} />
                    </ButtonWrapper>
                  </RoleRequired>
                )}
              </MultiButtonsWrapper>
            </Segment.Group>
            <RoleRequired
              staff={true}
              permission={permissions.estateCustomerAdd}
            >
              <Segment.Group>
                {profileCards}
                <Segment>
                  <AddProfileButtonWrapper>
                    <AddCustomerModal estate={estate} />
                  </AddProfileButtonWrapper>
                </Segment>
              </Segment.Group>
            </RoleRequired>
            <RoleRequired staff={true} permission={permissions.noteEdit}>
              <StyledHeader forwardedAs="h3">{t("block.notes")}</StyledHeader>
              <StyledNotes entity={estate} entityType="estate" />
            </RoleRequired>
          </StyledColumn>
          <StyledColumn largeScreen={12} computer={10} tablet={16}>
            <Grid>
              <Grid.Row>
                <Grid.Column>
                  {!estate.charging_enabled && (
                    <Message
                      warning
                      icon={"history"}
                      header={t("estate.auto_bill_off")}
                      content={t("estate.no_periodic_bill")}
                    />
                  )}
                  <DetailsHeader estate={estate} />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <StyledHeaderAttr forwardedAs="h3">
                    {t("billing.attributes")}
                  </StyledHeaderAttr>
                  <AttributeList block={block} estate={estate} />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <StyledHeader forwardedAs="h3">
                    {t("billing.obligations")}
                  </StyledHeader>
                  <ChargeList estate={estate} />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <StyledHeader forwardedAs="h3">
                    {t("labels.payments")}
                  </StyledHeader>
                  <SettlementListEstate estate={estate} />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </StyledColumn>
        </Grid.Row>
      </Grid>
    </React.Fragment>
  );
};

export default EstateDetails;
